
import { useState } from 'react'
import { useSiteThemes, useSiteConfig, useUpdateSiteConfig } from '@/hooks/useCMS'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'
import { Palette, Type, Layout, Layers, Save } from 'lucide-react'

export const CMSThemeManager = () => {
  const { data: themes } = useSiteThemes()
  const { data: config } = useSiteConfig()
  const updateConfig = useUpdateSiteConfig()
  
  const [formData, setFormData] = useState<{ [key: string]: string }>({})

  const handleSave = async () => {
    try {
      const updates = Object.entries(formData).map(([key, value]) => {
        const [section, configKey] = key.split('.')
        return updateConfig.mutateAsync({
          section,
          key: configKey,
          value,
          type: 'text'
        })
      })

      await Promise.all(updates)
      toast.success('Thème sauvegardé avec succès!')
      setFormData({})
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde')
      console.error(error)
    }
  }

  const handleChange = (section: string, key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [`${section}.${key}`]: value
    }))
  }

  const getValue = (section: string, key: string) => {
    const formKey = `${section}.${key}`
    if (formData[formKey] !== undefined) {
      return formData[formKey]
    }
    return config?.find(item => item.section === section && item.key === key)?.value || ''
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Design System</h2>
        <Button onClick={handleSave} disabled={Object.keys(formData).length === 0}>
          <Save className="w-4 h-4 mr-2" />
          Sauvegarder
        </Button>
      </div>

      <Tabs defaultValue="typography" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="typography">
            <Type className="w-4 h-4 mr-2" />
            Typographie
          </TabsTrigger>
          <TabsTrigger value="colors">
            <Palette className="w-4 h-4 mr-2" />
            Couleurs
          </TabsTrigger>
          <TabsTrigger value="spacing">
            <Layout className="w-4 h-4 mr-2" />
            Espacement
          </TabsTrigger>
          <TabsTrigger value="effects">
            <Layers className="w-4 h-4 mr-2" />
            Effets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="typography" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Polices de caractères</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Police des titres</Label>
                  <Select 
                    value={getValue('typography', 'heading_font')}
                    onValueChange={(value) => handleChange('typography', 'heading_font', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter</SelectItem>
                      <SelectItem value="Roboto">Roboto</SelectItem>
                      <SelectItem value="Poppins">Poppins</SelectItem>
                      <SelectItem value="Montserrat">Montserrat</SelectItem>
                      <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                      <SelectItem value="Merriweather">Merriweather</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Police du contenu</Label>
                  <Select 
                    value={getValue('typography', 'body_font')}
                    onValueChange={(value) => handleChange('typography', 'body_font', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter</SelectItem>
                      <SelectItem value="Roboto">Roboto</SelectItem>
                      <SelectItem value="Open Sans">Open Sans</SelectItem>
                      <SelectItem value="Lato">Lato</SelectItem>
                      <SelectItem value="Source Sans Pro">Source Sans Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Taille de police de base (px)</Label>
                <div className="flex items-center space-x-4">
                  <Slider
                    value={[parseInt(getValue('typography', 'font_size_base') || '16')]}
                    onValueChange={([value]) => handleChange('typography', 'font_size_base', value.toString())}
                    min={12}
                    max={24}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-500 w-12">
                    {getValue('typography', 'font_size_base') || '16'}px
                  </span>
                </div>
              </div>

              {/* Aperçu typographie */}
              <div className="mt-6 p-6 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-semibold mb-4">Aperçu</h3>
                <div 
                  style={{
                    fontFamily: getValue('typography', 'heading_font') || 'Inter',
                    fontSize: `${parseInt(getValue('typography', 'font_size_base') || '16') * 2}px`
                  }}
                >
                  <h1 className="text-4xl font-bold mb-2">Titre H1</h1>
                  <h2 className="text-3xl font-semibold mb-2">Titre H2</h2>
                  <h3 className="text-2xl font-medium mb-4">Titre H3</h3>
                </div>
                <p 
                  style={{
                    fontFamily: getValue('typography', 'body_font') || 'Inter',
                    fontSize: `${getValue('typography', 'font_size_base') || '16'}px`
                  }}
                  className="text-gray-700"
                >
                  Ceci est un exemple de paragraphe utilisant la police de contenu sélectionnée. 
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="colors" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { key: 'primary_color', label: 'Couleur Principale', description: 'Couleur principale de votre marque' },
              { key: 'secondary_color', label: 'Couleur Secondaire', description: 'Couleur d\'accent secondaire' },
              { key: 'accent_color', label: 'Couleur d\'Accent', description: 'Pour les boutons et liens' },
              { key: 'background_color', label: 'Couleur de Fond', description: 'Arrière-plan principal' },
              { key: 'text_color', label: 'Couleur du Texte', description: 'Couleur du texte principal' },
            ].map((color) => (
              <Card key={color.key}>
                <CardHeader>
                  <CardTitle className="text-sm">{color.label}</CardTitle>
                  <p className="text-xs text-gray-500">{color.description}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={getValue('colors', color.key)}
                      onChange={(e) => handleChange('colors', color.key, e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={getValue('colors', color.key)}
                      onChange={(e) => handleChange('colors', color.key, e.target.value)}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                  <div 
                    className="w-full h-12 rounded border"
                    style={{ backgroundColor: getValue('colors', color.key) }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Palette d'harmonie automatique */}
          <Card>
            <CardHeader>
              <CardTitle>Harmonie des couleurs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {/* Génération automatique de couleurs harmonieuses basées sur la couleur principale */}
                {Array.from({ length: 5 }, (_, i) => {
                  const baseColor = getValue('colors', 'primary_color') || '#1e3a8a'
                  const hue = (parseInt(baseColor.slice(1), 16) + i * 30) % 360
                  const harmonyColor = `hsl(${hue}, 70%, 50%)`
                  
                  return (
                    <div key={i} className="text-center">
                      <div 
                        className="w-full h-16 rounded border mb-2 cursor-pointer"
                        style={{ backgroundColor: harmonyColor }}
                        onClick={() => handleChange('colors', 'accent_color', harmonyColor)}
                      />
                      <span className="text-xs text-gray-500">Harmonie {i + 1}</span>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spacing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Espacement et mise en page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Espacement des sections (px)</Label>
                  <div className="flex items-center space-x-4">
                    <Slider
                      value={[parseInt(getValue('spacing', 'section_padding') || '64')]}
                      onValueChange={([value]) => handleChange('spacing', 'section_padding', value.toString())}
                      min={16}
                      max={128}
                      step={8}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500 w-12">
                      {getValue('spacing', 'section_padding') || '64'}px
                    </span>
                  </div>
                </div>

                <div>
                  <Label>Espacement du conteneur (px)</Label>
                  <div className="flex items-center space-x-4">
                    <Slider
                      value={[parseInt(getValue('spacing', 'container_padding') || '32')]}
                      onValueChange={([value]) => handleChange('spacing', 'container_padding', value.toString())}
                      min={8}
                      max={64}
                      step={4}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500 w-12">
                      {getValue('spacing', 'container_padding') || '32'}px
                    </span>
                  </div>
                </div>

                <div>
                  <Label>Largeur maximale (px)</Label>
                  <div className="flex items-center space-x-4">
                    <Slider
                      value={[parseInt(getValue('layout', 'max_width') || '1200')]}
                      onValueChange={([value]) => handleChange('layout', 'max_width', value.toString())}
                      min={800}
                      max={1600}
                      step={50}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500 w-16">
                      {getValue('layout', 'max_width') || '1200'}px
                    </span>
                  </div>
                </div>

                <div>
                  <Label>Colonnes de grille</Label>
                  <div className="flex items-center space-x-4">
                    <Slider
                      value={[parseInt(getValue('layout', 'grid_columns') || '12')]}
                      onValueChange={([value]) => handleChange('layout', 'grid_columns', value.toString())}
                      min={6}
                      max={24}
                      step={2}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500 w-12">
                      {getValue('layout', 'grid_columns') || '12'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="effects" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Effets visuels</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Arrondi des bordures (px)</Label>
                  <div className="flex items-center space-x-4">
                    <Slider
                      value={[parseInt(getValue('effects', 'border_radius') || '8')]}
                      onValueChange={([value]) => handleChange('effects', 'border_radius', value.toString())}
                      min={0}
                      max={32}
                      step={2}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500 w-12">
                      {getValue('effects', 'border_radius') || '8'}px
                    </span>
                  </div>
                </div>

                <div>
                  <Label>Intensité des ombres</Label>
                  <Select 
                    value={getValue('effects', 'shadow_intensity')}
                    onValueChange={(value) => handleChange('effects', 'shadow_intensity', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      <SelectItem value="light">Légère</SelectItem>
                      <SelectItem value="medium">Moyenne</SelectItem>
                      <SelectItem value="strong">Forte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Aperçu des effets */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <Card 
                  style={{ 
                    borderRadius: `${getValue('effects', 'border_radius') || '8'}px`,
                    boxShadow: getValue('effects', 'shadow_intensity') === 'light' ? '0 1px 3px rgba(0,0,0,0.12)' :
                               getValue('effects', 'shadow_intensity') === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' :
                               getValue('effects', 'shadow_intensity') === 'strong' ? '0 10px 15px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  <CardContent className="p-4">
                    <div className="w-full h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded mb-2"></div>
                    <p className="text-sm">Aperçu des effets</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
